import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { getCreatorApiUrl } from '@client/components/api-urls';
import auth from '@client/components/auth';
import { formatISODate } from '@client/helpers/date-utils';
import { Async } from '@client/new/helpers/types';

export type useOtpPayoutsProps = {
  date: Date;
  creatorId: number;
};

export type OtpProgramRevenue = {
  name: string;
  unitsSold: number;
  revenue: number;
};

export type RefundsAndDisputes = {
  unitsSold: number;
  revenue: number;
};

export type OtpEarningsBreakdown = {
  date: string;
  programs: OtpProgramRevenue[];
  refundsAndDisputes: RefundsAndDisputes;
  totalOtpProgramsRevenue: number;
  payoutFee: number;
  totalPayout: number;
  paidAt?: string;
  hasPrograms: boolean;
};

const fetchOtpPayout = async (
  creatorId: number,
  date: Date,
): Promise<OtpEarningsBreakdown> => {
  const url = new URL(
    getCreatorApiUrl(`/one-time-purchase/payout/${creatorId}/dashboard`),
  );

  const response = await axios.get<OtpEarningsBreakdown>(url.toString(), {
    params: {
      date: formatISODate(date),
    },
    headers: {
      Authorization: auth.getToken(),
    },
  });

  return response.data;
};

export const useOtpPayouts = ({
  date,
  creatorId,
}: useOtpPayoutsProps): Async<OtpEarningsBreakdown> => {
  const { isFetched, data, isError } = useQuery(
    ['otp-payouts', creatorId, date],
    () => fetchOtpPayout(creatorId, date),
  );

  if (isError) {
    return {
      type: 'fetch-error',
      value: 'could not fetch data',
    };
  }

  if (isFetched && data) {
    return {
      type: 'fetched',
      value: data,
    };
  }

  return {
    type: 'fetching',
  };
};
